import { useLayoutEffect, useMemo, useRef, useState, type ReactNode, type ClipboardEvent, type DragEvent, type KeyboardEvent } from "react";
import { promptContentSchema, promptToPlainText, serializePortablePrompt, type PromptContent, type PromptSegment } from "@seedance/contracts";
import { deletePromptRange, parseExternalPromptText, parsePromptClipboard, PROMPT_CLIPBOARD_TYPE, promptLength, promptOffsetToPoint, promptPointToOffset, replacePromptRange, slicePrompt, type PromptRange } from "./prompt-edit-operations.js";
import { readPromptSelection, restorePromptSelection, promptRangeAtClientPoint } from "./prompt-dom.js";
import { PromptBindingStatus } from "./PromptBindingStatus.js";
export interface ReferenceRenderActions { range: PromptRange; onRebind(): void; onRemove(): void }
export interface StructuredPromptEditorProps {
  value: PromptContent; onChange(value: PromptContent): void;
  onRequestReference(range: PromptRange, query: string): void;
  ariaLabel: string; maxLength?: number;
  renderReference?(reference: Extract<PromptSegment, { type: "reference" }>, actions: ReferenceRenderActions): ReactNode;
  onUndo?(): void; onRedo?(): void;
}
interface CompositionBuffer { content: PromptContent; range: PromptRange; data: string; children: ReactNode }
interface DigitContinuation { slotId: string; caret: number }
const sameContent = (left: PromptContent, right: PromptContent): boolean => left === right || JSON.stringify(left) === JSON.stringify(right);
const collapsedAt = (content: PromptContent, offset: number): PromptRange => {
  const point = promptOffsetToPoint(content, Math.min(offset, promptLength(content)));
  return { start: point, end: point };
};

/** A typed picker request belongs to a proposal until the controlled host echoes it. */
export function createPromptQueryAcknowledgement() {
  let pending:{content:PromptContent;range:PromptRange;query:string}|undefined;
  return {
    cancel(){pending=undefined;},
    propose(content:PromptContent,caret:number){
      pending=undefined;
      const point=promptOffsetToPoint(content,caret), segment=content.segments[point.segmentIndex];
      const match=segment?.type === "text" ? /@([^\s@]*)$/u.exec(segment.text.slice(0,point.offset)) : null;
      if(match) pending=structuredClone({content,range:{start:{segmentIndex:point.segmentIndex,offset:point.offset-match[0].length},end:point},query:match[1]!});
    },
    acknowledge(content:PromptContent){
      const offered=pending;pending=undefined;
      return offered && sameContent(content,offered.content) ? {range:offered.range,query:offered.query} : undefined;
    }
  };
}

/** Additive editor: the host commits each onChange as one whole-document command. */
export function StructuredPromptEditor({ value, onChange, onRequestReference, ariaLabel, maxLength = 20_000, renderReference, onUndo, onRedo }: StructuredPromptEditorProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const current = useRef(value);
  const queryAcknowledgement = useRef(createPromptQueryAcknowledgement());
  const lastEmission = useRef<PromptContent | null>(null);
  const composition = useRef<CompositionBuffer | null>(null);
  const continuation = useRef<DigitContinuation | null>(null);
  const savedSelection = useRef<PromptRange | null>(null);
  const pendingSelection = useRef<PromptRange | null>(null);
  const pendingContent = useRef<PromptContent>(value);
  const pendingBase = useRef<PromptContent>(value);
  const lastValue = useRef(value);
  const [epoch, setEpoch] = useState(0);
  const [error, setError] = useState<string>();
  const [recoveryText, setRecoveryText] = useState<string>();

  const allocate = (): string => {
    const existing = new Set(current.current.segments.flatMap(segment => segment.type === "reference" ? [segment.slotId] : []));
    let id: string;
    do { id = crypto.randomUUID(); } while (existing.has(id));
    return id;
  };
  const selection = (): PromptRange | null => rootRef.current ? readPromptSelection(rootRef.current, current.current) : null;
  const cancelPendingEdit = (): void => {
    continuation.current = null;
    pendingSelection.current = null;
    lastEmission.current = null;
    queryAcknowledgement.current.cancel();
  };
  const fail = (message: string, rebuild = false): void => {
    setError(message);
    if (rebuild) { pendingSelection.current = savedSelection.current; pendingContent.current = current.current; setEpoch(number => number + 1); }
  };
  const retainComposition = (text: string): void => {
    if (text) setRecoveryText(previous => previous === undefined ? text : `${previous}\n${text}`);
  };
  const commit = (next: PromptContent, caret: number, message?: string, announce = false): boolean => {
    queryAcknowledgement.current.cancel();
    if (!promptContentSchema.safeParse(next).success) { fail("提示词结构无效，原内容已保留，请重试。"); return false; }
    if (promptToPlainText(next).length > maxLength) { fail("提示词长度超出限制，原内容已保留。"); return false; }
    pendingSelection.current = collapsedAt(next, caret);
    pendingBase.current = current.current;
    pendingContent.current = next;
    if (announce) queryAcknowledgement.current.propose(next,caret);
    setError(message);
    if (!sameContent(next, current.current)) {
      // A proposal is not canonical until the controlled value acknowledges it.
      lastEmission.current = next; onChange(next);
    }
    return true;
  };
  const insertText = (text: string, range: PromptRange, external = false, message?: string): boolean => {
    const content = current.current, start = promptPointToOffset(content, range.start), end = promptPointToOffset(content, range.end);
    const active = continuation.current;
    continuation.current = null;
    if (!external && /^\d+$/u.test(text) && start === end && active?.caret === start) {
      const index = content.segments.findIndex(segment => segment.type === "reference" && segment.slotId === active.slotId);
      const segment = content.segments[index];
      if (segment?.type === "reference" && segment.label.length + text.length <= 255) {
        const next = { segments: content.segments.map((item, i) => i === index ? { ...segment, label: segment.label + text } : item) };
        const committed = commit(next, start);
        if (committed) continuation.current = active;
        return committed;
      }
    }
    // Parse only inserted input plus an unfinished mention immediately to its left.
    // This also handles one native insertion/composition containing an interior ordinal;
    // unrelated V3 literal text and all existing reference identities stay untouched.
    const previous = slicePrompt(content, 0, start).at(-1);
    const open = !external && previous?.type === "text" ? /@[^@\s]*$/u.exec(previous.text)?.[0] ?? "" : "";
    const prefix = /^@(图片|视频|音频)[1-9]\d*$/u.test(open) ? "" : open;
    const inserted = parseExternalPromptText(prefix + text, allocate);
    const localStart = start - prefix.length;
    const next = replacePromptRange(content, { start: promptOffsetToPoint(content, localStart), end: range.end }, inserted.segments);
    const caret = localStart + promptLength(inserted);
    const reference = inserted.segments.at(-1);
    if (!external && reference?.type === "reference" && /^(图片|视频|音频)[1-9]\d*$/u.test(reference.label)) {
      continuation.current = { slotId: reference.slotId, caret };
    }
    const committed = commit(next, caret, message, true);
    if (!committed) continuation.current = null;
    return committed;
  };
  const remove = (range: PromptRange, direction: "backward" | "forward"): void => {
    continuation.current = null;
    const content = current.current, start = promptPointToOffset(content, range.start), end = promptPointToOffset(content, range.end);
    const next = deletePromptRange(content, range, direction);
    const removed = promptLength(content) - promptLength(next);
    commit(next, start === end && direction === "backward" ? start - removed : start);
  };

  const beforeInput = (event: InputEvent): void => {
    event.stopPropagation();
    if (composition.current && (event.isComposing || event.inputType === "insertCompositionText" || event.inputType === "deleteCompositionText")) {
      if (event.data !== null) composition.current.data = event.data;
      return;
    }
    event.preventDefault();
    if (composition.current) return;
    // A new user edit supersedes any proposal the host has not acknowledged.
    if (pendingSelection.current) cancelPendingEdit();
    try {
      if (event.inputType === "historyUndo" || event.inputType === "historyRedo") {
        continuation.current = null; (event.inputType === "historyUndo" ? onUndo : onRedo)?.(); return;
      }
      const range = selection();
      if (!range) { fail("无法定位提示词选区，请重新点击编辑位置。"); return; }
      savedSelection.current = range;
      if (event.inputType === "insertText" || event.inputType === "insertReplacementText") insertText(event.data ?? "", range);
      else if (event.inputType === "insertParagraph" || event.inputType === "insertLineBreak") insertText("\n", range);
      else if (event.inputType === "deleteContentBackward") remove(range, "backward");
      else if (event.inputType === "deleteContentForward" || event.inputType === "deleteByCut") remove(range, "forward");
      else fail("暂不支持此输入操作，原内容已保留；请使用普通输入、退格或粘贴重试。");
    } catch { fail("提示词编辑未完成，原内容已保留，请重新选择后重试。", true); }
  };
  const keyboard = (event: KeyboardEvent<HTMLDivElement>): void => {
    event.stopPropagation();
    if (composition.current || event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.target !== event.currentTarget && (event.target as HTMLElement).closest("button, textarea")) return;
    const command = event.metaKey || event.ctrlKey, key = event.key.toLowerCase();
    if (command && (key === "z" || key === "y")) {
      event.preventDefault(); cancelPendingEdit();
      (key === "y" || event.shiftKey ? onRedo : onUndo)?.(); return;
    }
    if (event.key.startsWith("Arrow") || ["Home", "End", "PageUp", "PageDown", "Escape", "Tab"].includes(event.key) || command) cancelPendingEdit();
  };
  const clipboard = (event: ClipboardEvent<HTMLDivElement>, cut: boolean): void => {
    event.stopPropagation(); event.preventDefault();
    if (composition.current) return;
    const range = selection(); if (!range) return;
    const start = promptPointToOffset(current.current, range.start), end = promptPointToOffset(current.current, range.end);
    const selected = { segments: slicePrompt(current.current, start, end) };
    event.clipboardData.setData("text/plain", serializePortablePrompt(selected));
    event.clipboardData.setData(PROMPT_CLIPBOARD_TYPE, JSON.stringify(selected));
    if (cut && start !== end) remove(range, "forward");
  };
  const paste = (event: ClipboardEvent<HTMLDivElement>): void => {
    event.preventDefault(); event.stopPropagation(); cancelPendingEdit();
    if (composition.current) return;
    const range = selection(); if (!range) return;
    try {
      const parsed = parsePromptClipboard(event.clipboardData.getData(PROMPT_CLIPBOARD_TYPE), event.clipboardData.getData("text/plain"), allocate);
      const start = promptPointToOffset(current.current, range.start);
      commit(replacePromptRange(current.current, range, parsed.content.segments), start + promptLength(parsed.content), parsed.error);
    } catch { fail("粘贴内容无法安全解析，原内容已保留，请检查文本后重试。"); }
  };
  const drop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault(); event.stopPropagation(); cancelPendingEdit();
    if (composition.current) return;
    const root = rootRef.current; if (!root) return;
    if (event.dataTransfer.files.length || !event.dataTransfer.types.includes("text/plain")) { fail("请使用素材选择器添加素材；本编辑区仅接受文本拖放。"); return; }
    const range = promptRangeAtClientPoint(root, current.current, event.clientX, event.clientY);
    if (!range) { fail("无法定位拖放位置，请点击编辑区后粘贴文本。"); return; }
    try { root.focus(); insertText(event.dataTransfer.getData("text/plain"), range, true); }
    catch { fail("拖放文本无法安全解析，原内容已保留。"); }
  };

  const children = useMemo(() => value.segments.map((segment, segmentIndex) => {
    const range = { start: { segmentIndex, offset: 0 }, end: { segmentIndex, offset: 1 } };
    if (segment.type === "text") return <span key={`text-${segmentIndex}`} data-prompt-segment={segmentIndex} data-prompt-kind="text">{segment.text}</span>;
    const actions: ReferenceRenderActions = {
      range,
      // A semantic slot label is not an asset-name query; explicit rebind offers all candidates.
      onRebind: () => { if (!composition.current) onRequestReference(range, ""); },
      onRemove: () => { if (!composition.current) { const start = promptPointToOffset(current.current, range.start); commit(replacePromptRange(current.current, range, []), start); } }
    };
    return <span key={`reference-${segment.slotId}-${segmentIndex}`} data-prompt-segment={segmentIndex} data-prompt-kind="reference" data-slot-id={segment.slotId} contentEditable={false} className="structured-prompt-editor__reference">
      {renderReference ? renderReference(segment, actions) : <PromptBindingStatus reference={segment} onRebind={actions.onRebind} onRemove={actions.onRemove} />}
    </span>;
  }), [value, renderReference, onRequestReference, onChange, maxLength]);

  useLayoutEffect(() => {
    const root = rootRef.current; if (!root) return;
    // Publish only committed controlled values. An abandoned concurrent render must
    // not replace canonical input or consume a pending caret/query/continuation.
    // Document commands may clone echoes, so compare content as well as identity.
    if (!sameContent(value, lastValue.current)) {
      if (!lastEmission.current || !sameContent(value, lastEmission.current)) {
        continuation.current = null;
        if (!composition.current && savedSelection.current) {
          const start = Math.min(promptPointToOffset(lastValue.current, savedSelection.current.start), promptLength(value));
          const end = Math.min(promptPointToOffset(lastValue.current, savedSelection.current.end), promptLength(value));
          savedSelection.current = { start: promptOffsetToPoint(value, start), end: promptOffsetToPoint(value, end) };
          // Focus on a chip button is a control interaction, not an active text caret.
          pendingSelection.current = root.ownerDocument.activeElement === root ? savedSelection.current : null;
          pendingContent.current = value;
        }
      }
      lastValue.current = value;
    }
    if (!composition.current) current.current = value;
    // Local native listener: React's synthetic beforeinput does not expose all InputEvent types.
    root.addEventListener("beforeinput", beforeInput);
    // An unrelated render can commit the old controlled value before the edit echo.
    // It is not a rejection: keep the proposal's caret until the host acknowledges it.
    // User navigation/new input cancels it, and a different canonical value supersedes it.
    const awaitingEcho = pendingSelection.current && !sameContent(value, pendingContent.current) && sameContent(value, pendingBase.current);
    if (!composition.current && pendingSelection.current && !awaitingEcho) {
      if (!sameContent(value, pendingContent.current)) {
        continuation.current = null;
        // A host may decline an operation yet rerender (e.g. to show a picker).
        // Keep the actual accepted DOM selection, never apply a proposed range.
        pendingSelection.current = readPromptSelection(root, value) ?? collapsedAt(value, 0);
      }
      const acceptedSelection = pendingSelection.current;
      if (root.ownerDocument.activeElement === root || savedSelection.current) {
        // focus() may synchronously dispatch blur and cancel the mutable pending ref.
        root.focus(); restorePromptSelection(root, value, acceptedSelection);
      }
      savedSelection.current = acceptedSelection;
      pendingSelection.current = null;
    }
    if (!composition.current && !awaitingEcho) {
      const acceptedQuery=queryAcknowledgement.current.acknowledge(value);
      if(acceptedQuery) onRequestReference(acceptedQuery.range,acceptedQuery.query);
    }
    return () => root.removeEventListener("beforeinput", beforeInput);
  });

  return <div className="structured-prompt-editor nodrag nowheel" onKeyDown={keyboard} onKeyUp={event => event.stopPropagation()} onPointerDown={event => event.stopPropagation()}>
    <div key={epoch} ref={rootRef} role="textbox" contentEditable suppressContentEditableWarning aria-label={ariaLabel} aria-multiline="true" aria-invalid={Boolean(error)} className="structured-prompt-editor__input"
      onCopy={event => clipboard(event, false)} onCut={event => clipboard(event, true)} onPaste={paste} onDrop={drop}
      onDragOver={event => { event.preventDefault(); event.stopPropagation(); }} onDragStart={event => { event.preventDefault(); event.stopPropagation(); }}
      onPointerDown={cancelPendingEdit} onKeyDown={keyboard}
      onKeyUp={() => { if (!composition.current) savedSelection.current = selection(); }}
      onMouseUp={() => { if (!composition.current) savedSelection.current = selection(); }}
      onSelect={() => { if (!composition.current) savedSelection.current = selection(); }}
      onBlur={cancelPendingEdit}
      onInput={event => { event.stopPropagation(); if (!composition.current) fail("浏览器执行了不支持的输入，原内容已恢复，请重试。", true); }}
      onCompositionStart={event => {
        event.stopPropagation(); cancelPendingEdit();
        const range = selection();
        if (range) { savedSelection.current = range; composition.current = { content: current.current, range, data: "", children }; }
      }}
      onCompositionEnd={event => {
        event.stopPropagation();
        const buffer = composition.current; if (!buffer) return;
        composition.current = null;
        const text = event.data;
        if (!sameContent(value, buffer.content)) {
          current.current = value; retainComposition(text); fail("输入期间内容已更新，本次输入未覆盖新内容。请复制待恢复文本后重试。");
        } else {
          current.current = buffer.content;
          try { if (text && !insertText(text, buffer.range)) retainComposition(text); }
          catch { retainComposition(text); fail("输入法内容无法提交，原内容已保留，请复制待恢复文本后重试。"); }
        }
        setEpoch(number => number + 1);
      }}
    >{composition.current ? composition.current.children : children}{(composition.current?.content ?? value).segments.length ? null : <br />}</div>
    {error ? <span role="alert" className="structured-prompt-editor__error">{error}</span> : null}
    {recoveryText !== undefined ? <div className="structured-prompt-editor__recovery">
      <label>待恢复的输入法文本<textarea aria-label="待恢复的输入法文本" readOnly value={recoveryText} /></label>
      <button type="button" onClick={() => setRecoveryText(undefined)}>清除待恢复文本</button>
    </div> : null}
  </div>;
}
